/**
 * @alias components.core.CaptionsPlugin
 * @classdesc Adds support for in game captions. Renders captions over the game within a dom element
 *
 * The dom element is styled following the below defaults
 *
 * <pre>
 {
    fontSize: 24,
    fontFamily: 'sans-serif',
    color: 'white',
    background: 'black',
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    bottom: 10,
    position: 'absolute',
    left : 50%;
    transform : translateX(-50%)
 }
 * </pre>
 * <br>
 *
 * ***This is a core jgg component, and so will be available to any game extending {@link AppBase}.***
 *
 * <pre>
 * this.game.captions
 * </pre>
 *
 * The default syle can be overridden on the [getConfig()]{@link getConfigBase} component options.
 * It should the follow strcture of a javascript css style object
 *
 * <pre>
 * components : {
 *  captions : { data : { ...style } }
 * }
 * </pre>
 *
 * Or by calling the [setStyle]{@link components.core.CaptionsPlugin#setStyle} method
 *
 * <pre>
 * this.game.captions.setStyle({ ...style })
 * </pre>
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Global Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.install('key', CaptionsPlugin, true, 'scenekey' || null, { ...style } );
 * </pre>
 *
 */
class CaptionsPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);
    this._style = {
      fontSize: '24px',
      fontFamily: 'sans-serif',
      color: 'white',
      background: 'black',
      paddingTop: '5px',
      paddingBottom: '5px',
      paddingLeft: '10px',
      paddingRight: '10px',
      bottom: '10px',
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
    };
    this._timeout = false;
    this._enabled = false;
    this._captionsToShow = [];
    this._durationMin = 1500;
    this._durationMax = false;
    this._durationChar = 300;
  }

  init(config) {
    if (config) {
      this.setStyle(config);
    }
    this._domElement = document.createElement('div');
    this._styleSubtitle();
  }

  /**
   * @description retrieves the current active scene. Used to set this._timeout on this scene
   */
  get currentScene() {
    return this.game.sceneController.getCurrentScene();
  }

  /**
   * @description set the minimum duration that a caption will display for ( if no duration is supplied )
   * @default 1500
   */
  set durationMinimum(value) {
    this._durationMin = value;
  }

  /**
   * @description set the maximum duration that a caption will display for ( disabled by default )
   * @default false
   */
  set durationMaximum(value) {
    this._durationMax = value;
  }

  /**
   * @description set the duration for each character in the caption. <br>
   * If no duration is supplied the duration is calculated as ( value x caption.length )
   */
  set durationPerChar(value) {
    this._durationChar = value;
  }

  /**
   * Enable / Disable captions
   * @default false
   */
  set enabled(value) {
    this._enabled = value;
  }

  /**
   *
   * @description set captions style, formatted in a css valid javascript object
   * @default see example
   *
   */
  set style(value) {
    this.setStyle(value);
    this._styleSubtitle();
  }

  /**
   *  @description set caption font size
   *  @default 24
   */
  set fontSize(value) {
    this._style.fontSize = value + 'px';
    this._styleSubtitle();
  }

  /**
   * @description set caption font family
   * @default sans-serif
   */
  set fontFamily(value) {
    this._style.fontFamily = value;
    this._styleSubtitle();
  }

  /**
   *  @description set caption font colour
   *  @default white
   */
  set colour(value) {
    this._style.color = value;
    this._styleSubtitle();
  }

  /**
   *  @description set caption background colour
   *  @default black
   */
  set background(value) {
    this._style.background = value;
    this._styleSubtitle();
  }

  /** @description set caption background padding ( all sides ) */
  set padding(value) {
    this._style.paddingTop = value + 'px';
    this._style.paddingBottom = value + 'px';
    this._style.paddingLeft = value + 'px';
    this._style.paddingRight = value + 'px';
    this._styleSubtitle();
  }

  /**
   * @description set caption background top and bottom padding
   * @default 5
   */
  set paddingVertical(value) {
    this._style.paddingTop = value + 'px';
    this._style.paddingBottom = value + 'px';
    this._styleSubtitle();
  }

  /**
   * @description set caption background left and right padding
   * @default 10
   */
  set paddingHorizontal(value) {
    this._style.paddingLeft = value + 'px';
    this._style.paddingRight = value + 'px';
    this._styleSubtitle();
  }

  /**
   * @description set caption distance from screen edge
   * @default 10
   * */
  set scenePadding(value) {
    this._style.bottom = value + 'px';
    this._styleSubtitle();
  }

  /**
   * Set the styling of the captions
   * @param {Object} style a style object in the format of javscript css styling
   *
   */
  setStyle(style = {}) {
    this._style = {
      ...this._style,
      ...style,
    };
  }

  /**
   * @description Show a caption for a defined amount of time. By default the duration will be calculated according to caption length
   * @param {string|Array} copy caption or captions to be displayed
   * @param {number} [duration] duration to show caption for
   * @example <caption>Example of the different way in which you can trigger captions</caption>

   // show a single caption using default duration
   scene.captions.show('caption string');

   // show an array of captions using default duration
   scene.captions.show(['caption one', 'caption two'])

   // show an array of captions with defined duration for each caption
   scene.captions.show([
   {copy : 'caption one', duration : 1000},
   {copy : 'caption two', duration : 2500}
   ])

   // mix and match
   scene.captions.show([
   'caption one',
   {copy : 'caption two', duration : 1000},
   {copy : 'caption three', duration : 2500}
   ])

   */
  show(copy, duration) {
    if (!this._enabled || !this.currentScene) return;
    this._timeout && this._timeout.remove();
    if (Array.isArray(copy)) {
      copy.forEach((entry) => {
        if (typeof entry === 'string') {
          this._captionsToShow.push({ copy: entry, duration });
        } else if (entry.hasOwnProperty('copy')) {
          this._captionsToShow.push({ copy: entry.copy, duration: entry.duration || duration });
        }
      });
    } else {
      this._captionsToShow.push({ copy, duration });
    }
    if (!this._timeout && !this._intervalTimeout) {
      this._showNextCaption();
    }
  }

  /**
   * @description hide any active caption and clears the queue
   */
  hide() {
    this._clearCaptionTimeout();
    this._captionsToShow = [];
    try {
      this.game.scale.parent.removeChild(this._domElement);
    } catch (err) {}
  }

  /** @private */
  _showNextCaption() {
    if (!this._enabled || !this.currentScene) {
      this.hide();
      return;
    }
    const caption = this._captionsToShow.shift();
    if (caption) {
      this._clearCaptionTimeout();
      const { copy, duration } = caption;
      this._domElement.innerHTML = copy;
      this.game.scale.parent.appendChild(this._domElement);

      let showDuration = duration || Math.min(this._durationMin, this._durationChar * copy.length);
      if (this._durationMax) {
        showDuration = Math.min(showDuration, this._durationMax);
      }
      this._setCaptionTimeout(showDuration);
    } else {
      this.hide();
    }
  }

  /** @private */
  _setCaptionTimeout(showDuration) {
    if (this.currentScene) {
      this._timeout = this.currentScene.time.delayedCall(
        showDuration,
        this._showNextCaption,
        null,
        this
      );
    }
  }

  /** @private */
  _clearCaptionTimeout() {
    if (this._timeout) {
      this._timeout.remove();
    }
    this._timeout = null;
  }

  /** @private */
  _styleSubtitle() {
    Object.keys(this._style).forEach((key) => {
      this._domElement.style[key] = this._style[key];
    });
  }
}

export { CaptionsPlugin };
